import {forwardRef, useEffect, useState} from "react";
import {Box, BoxProps, Text} from "@mantine/core";
import classes from "./FocusModeView.module.css";
import cx from "clsx";
import dayjs, {Dayjs} from "dayjs";
import {
    IGroupStep,
    IProtocolStep,
    IProtocolStepKind,
    IProtocolStepStatus,
    TProtocolStepId,
} from "@/protocols/types/protocol.ts";
import {DeepPartial} from "@/utils/types/DeepPartial.ts";
import {RecorderStepView} from "./steps/RecorderStepView.tsx";
import {IEvent, TEventId, TSubjectId} from "@/common/types";
import {IProtocolStepEvent} from "@/protocols/types/IProtocolStepEvent.ts";
import {openConfirmModal} from "@/protocols/modals";

import {CountdownDisplay, FocusModeBanner, FocusModeNavigation, TimerButton, UpNextNotification,} from "./components";
import {CountdownState} from "./components/CountdownDisplay";
import commonClasses from "@/common/common.module.css";
import {IconX} from "@tabler/icons-react";

interface FocusModeViewProps extends BoxProps {
    prevStep?: IProtocolStep;
    step: IProtocolStep;
    nextStep?: IProtocolStep;
    index: number;
    subjectId: TSubjectId;
    protocolStepEvents: Record<TProtocolStepId, Array<IEvent>>;
    onDeleteEvent: (id: TEventId) => void;
    setViewMode: (mode: "normal" | "focus") => void;
    onNext: () => void;
    onPrev: () => void;
    startTime?: Dayjs;
    timezone: string;
    onStepChange: (stepId: TProtocolStepId, updates: DeepPartial<IProtocolStep>) => void;
    orderedSteps: Array<IProtocolStep>;
    onJumpToIndex: (index: number) => void;
    /**
     * Single creation hook forwarded to the rendered RecorderStepView's
     * EventRecorder. The caller decides how to persist (pending-writes,
     * direct, etc).
     */
    createEvent?: (
        eventData: IProtocolStepEvent,
    ) => Promise<IProtocolStepEvent>
    countDownState: CountdownState;
    onStartCountdown: (duration: number, description: string) => void;
    onStopCountdown: (state: CountdownState) => void;
    onCancelCountdown: () => void;
}

export const FocusModeView = forwardRef<HTMLDivElement, FocusModeViewProps>((
    {
        prevStep,
        step,
        nextStep,
        index,
        subjectId,
        protocolStepEvents,
        onDeleteEvent,
        onStepChange,
        setViewMode,
        onNext,
        onPrev,
        timezone,
        startTime,
        orderedSteps,
        onJumpToIndex,
        createEvent,
        countDownState,
        onStartCountdown,
        onStopCountdown,
        onCancelCountdown,
        ...props
    },
    ref
) => {
    // ==========================================================================
    // Timer State (inline implementation - not using useTimer hook)
    // ==========================================================================
    const [sessionDuration, setSessionDuration] = useState(() =>
        dayjs().tz(timezone!, false).diff(startTime ?? dayjs().tz(timezone!, false))
    );

    // Session duration ticker - runs independently of countdown
    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setSessionDuration(dayjs().tz(timezone!, false).diff(startTime));
        }, 1000);
        return () => clearInterval(intervalId);
    }, [timezone, startTime]);

    // ==========================================================================
    // Skip Group Handler
    // ==========================================================================
    const handleSkipAll = () => {
        // FIXED: Added type guard
        if (step.kind !== IProtocolStepKind.GROUP) return;

        const group = step as IGroupStep;
        const memberCount = group.memberStepIds.length;

        openConfirmModal({
            title: <Text ta="center" fw="bold">Skip Group</Text>,
            children: (
                <Text>Skip "{group.name}" and {memberCount} member step{memberCount !== 1 ? "s" : ""}?</Text>
            ),
            onConfirm: () => {
                onStepChange(step.id, {status: IProtocolStepStatus.SKIPPED});
                group.memberStepIds.forEach(memberId => {
                    onStepChange(memberId, {status: IProtocolStepStatus.SKIPPED});
                });
                onJumpToIndex(index + 1 + memberCount);
            }
        });
    };

    // ==========================================================================
    // Navigation Button State
    // ==========================================================================
    const isNextGroup = nextStep?.kind === IProtocolStepKind.GROUP;
    const isGroup = step?.kind === IProtocolStepKind.GROUP;

    let activeButtonText: string;
    if (!isGroup && index === 0) {
        activeButtonText = "Next";
    } else if (isNextGroup) {
        activeButtonText = "Finish Group";
    } else if (isGroup) {
        activeButtonText = "Start Group";
    } else {
        activeButtonText = "Next";
    }

    let activeButtonDisabled: boolean = !nextStep ||
        (!isGroup &&
            step.status !== IProtocolStepStatus.COMPLETED &&
            step.status !== IProtocolStepStatus.SKIPPED &&
            !countDownState.counting);

    if (nextStep && step.status === IProtocolStepStatus.SKIPPED) {
        activeButtonText = "Skip";
    } else if (!nextStep) {
        activeButtonText = "Finish Protocol";
        activeButtonDisabled = step.status !== IProtocolStepStatus.COMPLETED &&
            step.status !== IProtocolStepStatus.SKIPPED;
    }

    // ==========================================================================
    // Render
    // ==========================================================================
    return (
        <Box
            className={cx(commonClasses.strongBlur, commonClasses.surfaceOverlay)}
            style={{position: "absolute", top: "0", left: "0", width: "100%", height: "100%", zIndex: 1000}}
        >
            <Box className={cx(classes.focusTitleBar)}>
                <FocusModeBanner
                    sessionDuration={sessionDuration}
                    currentTime={dayjs().tz(timezone!, false).format("HH:mm:ss z")}
                />
                <Box className={cx(classes.closeButton)} onClick={()=> setViewMode("normal")}>
                    <IconX/>
                </Box>
            </Box>
            <Box ref={ref} {...props} className={cx(classes.focusMode)}>

                {countDownState.counting && (
                    <CountdownDisplay
                        countDownState={countDownState}
                        onStop={onStopCountdown}
                        onCancel={onCancelCountdown}
                    />
                )}

                <Box className={cx(classes.focusModeInner)}>
                    <FocusModeNavigation
                        hasPrev={!!prevStep}
                        stepName={step.name}
                        nextButtonText={activeButtonText}
                        nextButtonDisabled={activeButtonDisabled}
                        showSkipAll={isGroup}
                        onPrev={onPrev}
                        onNext={onNext}
                        onSkipAll={handleSkipAll}
                    />

                    <RecorderStepView
                        key={step.id}
                        className={cx(classes.stepView)}
                        step={step}
                        index={index}
                        subjectId={subjectId}
                        protocolStepEvents={protocolStepEvents}
                        onDeleteEvent={onDeleteEvent}
                        timezone={timezone}
                        onChange={(updates) => onStepChange(step.id, updates)}
                        titleComponent={<TimerButton step={step} isCounting={countDownState.counting}
                                                     onStart={onStartCountdown}/>}
                        focusMode={true}
                        createEvent={createEvent}
                        style={{
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            border: 0
                        }}
                    />

                    <Box className={cx(classes.bottomRow)}>
                        <UpNextNotification nextStep={nextStep}/>
                    </Box>
                </Box>

            </Box>
        </Box>
    );
});
